import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import GoogleSheetsService from '../services/GoogleSheetsService';

interface GoogleSheetsImportProps {
  onImportComplete: () => void;
  selectedSemesterId: number | null;
}

const sheetsService = GoogleSheetsService.getInstance();

const STORAGE_KEY = 'googleSheetUrl';

function mapRowToStudent(row: string[]) {
  const get = (i: number) => (row[i] || '').trim();
  const firstName = get(2);
  const familyName = get(1);

  return {
    time_submitted: get(0) ? new Date(get(0)).toISOString() : null,
    family_name: familyName,
    first_name: firstName,
    combined_name: `${firstName} ${familyName}`,
    address: get(3) || null,
    phone_number: get(4) || null,
    home_country: get(5) || null,
    email: get(6) || '',
    gender: get(7) || null,
    marital_status: get(8) || null,
    major: get(9) || null,
    program_level: get(10) || null,
    special_interests: get(11) || null,
    has_dietary_restrictions: get(12).toLowerCase().startsWith('yes'),
    dietary_restrictions_details: get(13) || null,
    willing_to_share: get(14).toLowerCase().startsWith('yes'),
    vaccination_status: get(15) || null,
  };
}

export const GoogleSheetsImport: React.FC<GoogleSheetsImportProps> = ({ onImportComplete, selectedSemesterId }) => {
  const [sheetUrl, setSheetUrl] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [lastN, setLastN] = useState(20);
  const [rows, setRows] = useState<string[][]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [status, setStatus] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [existingEmails, setExistingEmails] = useState<Set<string>>(new Set());

  useEffect(() => {
    setIsAuthenticated(sheetsService.isSignedIn());
  }, []);

  const handleUrlChange = (url: string) => {
    setSheetUrl(url);
    localStorage.setItem(STORAGE_KEY, url);
  };

  const handleAuth = async () => {
    setStatus('Connecting to Google Sheets...');
    try {
      const success = await sheetsService.authenticate();
      setIsAuthenticated(success);
      setStatus(success ? 'Connected to Google Sheets.' : 'Authentication failed.');
    } catch (err: any) {
      setStatus(`Auth error: ${err.message}`);
    }
  };

  const handleFetch = async () => {
    const spreadsheetId = sheetsService.parseSpreadsheetId(sheetUrl);
    if (!spreadsheetId) {
      setStatus('Invalid Google Sheets URL. Please paste the full URL from your browser.');
      return;
    }

    setIsFetching(true);
    setStatus('Fetching rows...');
    try {
      const [fetched, emailsResult] = await Promise.all([
        sheetsService.fetchRows(spreadsheetId, 'Form Responses 1', lastN),
        supabase.from('students').select('email'),
      ]);

      const existing = new Set(
        (emailsResult.data || [])
          .map((s: { email: string }) => s.email.toLowerCase().trim())
          .filter(Boolean)
      );
      setExistingEmails(existing);

      setRows(fetched);
      // Pre-select only rows whose email is not already in the DB
      const newRowIndices = new Set(
        fetched
          .map((row, i) => (!existing.has((row[6] || '').toLowerCase().trim()) ? i : -1))
          .filter(i => i >= 0)
      );
      setSelected(newRowIndices);

      const dupeCount = fetched.length - newRowIndices.size;
      setStatus(
        `Fetched ${fetched.length} rows.` +
        (dupeCount > 0 ? ` ${dupeCount} already imported (deselected).` : '')
      );
    } catch (err: any) {
      setStatus(`Fetch error: ${err.message}`);
      setRows([]);
    } finally {
      setIsFetching(false);
    }
  };

  const toggleRow = (index: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map((_, i) => i)));
    }
  };

  const handleImport = async () => {
    const selectedRows = rows.filter((_, i) => selected.has(i));
    if (selectedRows.length === 0) {
      setStatus('No rows selected.');
      return;
    }

    setIsImporting(true);
    setStatus(`Importing ${selectedRows.length} students...`);
    try {
      const students = selectedRows.map(row => ({
        ...mapRowToStudent(row),
        semester_id: selectedSemesterId,
      }));
      const { error } = await supabase.from('students').insert(students);
      if (error) throw new Error(error.message);

      setStatus(`Successfully imported ${students.length} students!`);
      // Update existing emails so just-imported rows show as "Already imported"
      const newEmails = new Set(existingEmails);
      students.forEach(s => { if (s.email) newEmails.add(s.email.toLowerCase().trim()); });
      setExistingEmails(newEmails);
      setSelected(new Set());
      onImportComplete();
    } catch (err: any) {
      setStatus(`Import error: ${err.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const cellStyle: React.CSSProperties = {
    padding: '6px 10px',
    borderBottom: '1px solid #444',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px',
  };

  return (
    <div style={{ marginBottom: '30px' }}>
      <h3>Import Students from Google Sheets</h3>

      {/* Sheet URL input */}
      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', color: '#ccc', marginBottom: '4px', fontSize: '0.9em' }}>
          Google Sheet URL
        </label>
        <input
          type="text"
          value={sheetUrl}
          onChange={e => handleUrlChange(e.target.value)}
          placeholder="https://docs.google.com/spreadsheets/d/..."
          style={{
            width: '100%',
            padding: '8px',
            backgroundColor: '#333',
            color: 'white',
            border: '1px solid #555',
            borderRadius: '4px',
            fontSize: '0.9em',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Auth + Fetch controls */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
        {!isAuthenticated && (
          <button
            onClick={handleAuth}
            style={{
              padding: '8px 16px',
              backgroundColor: '#4285f4',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9em',
            }}
          >
            Connect Google Sheets
          </button>
        )}

        {isAuthenticated && (
          <>
            <label style={{ color: '#ccc', fontSize: '0.9em' }}>Last</label>
            <input
              type="number"
              value={lastN}
              onChange={e => setLastN(Math.max(1, parseInt(e.target.value) || 1))}
              min={1}
              style={{
                width: '60px',
                padding: '6px',
                backgroundColor: '#333',
                color: 'white',
                border: '1px solid #555',
                borderRadius: '4px',
                textAlign: 'center',
              }}
            />
            <label style={{ color: '#ccc', fontSize: '0.9em' }}>rows</label>
            <button
              onClick={handleFetch}
              disabled={isFetching}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isFetching ? 'not-allowed' : 'pointer',
                opacity: isFetching ? 0.7 : 1,
                fontSize: '0.9em',
              }}
            >
              {isFetching ? 'Fetching...' : 'Fetch Rows'}
            </button>
          </>
        )}
      </div>

      {/* Status message */}
      {status && (
        <p style={{
          color: status.includes('error') || status.includes('Error') || status.includes('Invalid')
            ? '#ff6b6b' : status.includes('Successfully') ? '#28a745' : '#ccc',
          fontSize: '0.9em',
          margin: '8px 0',
        }}>
          {status}
        </p>
      )}

      {/* Results table */}
      {rows.length > 0 && (
        <>
          <div style={{ overflowX: 'auto', marginBottom: '12px', border: '1px solid #444', borderRadius: '4px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em' }}>
              <thead>
                <tr style={{ backgroundColor: '#333', color: 'white' }}>
                  <th style={{ ...cellStyle, width: '30px' }}>
                    <input
                      type="checkbox"
                      checked={selected.size === rows.length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th style={cellStyle}>Submitted</th>
                  <th style={cellStyle}>Name</th>
                  <th style={cellStyle}>Email</th>
                  <th style={cellStyle}>Home Country</th>
                  <th style={cellStyle}>Major</th>
                  <th style={cellStyle}>Semester</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const email = (row[6] || '').toLowerCase().trim();
                  const alreadyImported = email !== '' && existingEmails.has(email);
                  return (
                    <tr
                      key={i}
                      onClick={() => toggleRow(i)}
                      style={{
                        backgroundColor: alreadyImported
                          ? '#e0e0e0'
                          : selected.has(i) ? '#c8e6c9' : '#f5f5f5',
                        color: alreadyImported ? '#888' : '#222',
                        cursor: 'pointer',
                      }}
                    >
                      <td style={cellStyle}>
                        <input
                          type="checkbox"
                          checked={selected.has(i)}
                          onChange={() => toggleRow(i)}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td style={cellStyle}>{row[0] || ''}</td>
                      <td style={cellStyle}>
                        {(row[2] || '')} {(row[1] || '')}
                        {alreadyImported && (
                          <span style={{
                            marginLeft: '6px',
                            fontSize: '0.75em',
                            backgroundColor: '#999',
                            color: 'white',
                            padding: '1px 5px',
                            borderRadius: '3px',
                          }}>
                            Already imported
                          </span>
                        )}
                      </td>
                      <td style={cellStyle}>{row[6] || ''}</td>
                      <td style={cellStyle}>{row[5] || ''}</td>
                      <td style={cellStyle}>{row[9] || ''}</td>
                      <td style={cellStyle}>{row[16] || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            onClick={handleImport}
            disabled={isImporting || selected.size === 0}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isImporting || selected.size === 0 ? 'not-allowed' : 'pointer',
              opacity: isImporting || selected.size === 0 ? 0.7 : 1,
              fontSize: '0.95em',
              fontWeight: 'bold',
            }}
          >
            {isImporting ? 'Importing...' : `Import ${selected.size} Selected Student${selected.size !== 1 ? 's' : ''}`}
          </button>
        </>
      )}
    </div>
  );
};
